import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ReportReason } from '@astrally/shared';
import { useApp } from '../../state/AppState';
import { useI18n } from '../../i18n';
import { ScreenHeader } from '../../components/common';
import type { FeedPost } from '../../lib/backend/types';
import { PostComposer } from './PostComposer';
import { PostCard } from './PostCard';

/**
 * The feed — the one shared surface in a product that is otherwise entirely
 * private.
 *
 * IT DOES NOT EXIST FOR PRIVATE ACCOUNTS. No tab (see components/TabBar.tsx),
 * no composer, no posts, and — this is the part that has to be true rather
 * than merely tidy — NO NETWORK CALL. An account that has not joined the
 * social layer never asks the server for a page, so there is no request to
 * refuse and nothing about other members reaches the device. The server and
 * firestore.rules refuse it anyway; this is the layer that means they never
 * have to.
 *
 * The screen still renders for such an account rather than bouncing to Home,
 * because a deep link (an old bookmark, a shared URL) that silently redirects
 * teaches nothing. It shows the same private-account explanation DiscoverScreen
 * shows, in the same shape, with the same way in — one concept explained once.
 *
 * COPY IS HARDCODED ENGLISH. app/src/i18n/en.ts is typed and was outside this
 * change's file scope; the keys to add are listed at the top of PostComposer.
 */

/** Matches FEED_PAGE_DEFAULT in functions/src/feed/feedFns.ts. */
const PAGE_SIZE = 15;

export function FeedScreen() {
  const { t } = useI18n();
  const app = useApp();
  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [failed, setFailed] = useState(false);

  const socialOn = app.user?.socialEnabled === true;
  const isPro = app.entitlement?.tier === 'pro';

  /**
   * Load the FIRST page, replacing whatever is on screen. Used on mount and
   * after posting. Paging is deliberately manual (a button, below) rather than
   * an infinite scroll: every page is a bounded, paid-for read, and a feed that
   * fetches while you idle at the bottom spends money nobody asked it to.
   */
  const loadFirst = useCallback(async () => {
    if (!socialOn) return;
    setLoading(true);
    setFailed(false);
    try {
      const page = await app.backend.listFeed({ limit: PAGE_SIZE });
      setPosts(page.posts);
      setCursor(page.cursor);
    } catch {
      // A flag rather than a message string, so the copy resolves at draw time
      // and a language change does not refetch the feed.
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [app.backend, socialOn]);

  useEffect(() => {
    void loadFirst();
  }, [loadFirst]);

  const loadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await app.backend.listFeed({ cursor, limit: PAGE_SIZE });
      // Deduped on id: a post deleted between two pages shifts the window, and
      // the same row arriving twice would give React duplicate keys.
      setPosts((prev) => {
        const seen = new Set(prev.map((p) => p.id));
        return [...prev, ...page.posts.filter((p) => !seen.has(p.id))];
      });
      setCursor(page.cursor);
    } catch {
      setFailed(true);
    } finally {
      setLoadingMore(false);
    }
  };

  const remove = async (post: FeedPost) => {
    await app.backend.deletePost(post.id);
    // Local removal rather than a refetch: the post is gone, and re-reading a
    // page to learn that costs the same as reading it the first time.
    setPosts((prev) => prev.filter((p) => p.id !== post.id));
  };

  const report = async (post: FeedPost, reason: ReportReason) => {
    await app.backend.reportPost(post.id, reason);
    setPosts((prev) => prev.filter((p) => p.id !== post.id));
  };

  /**
   * Pass the refusal through — PostCard renders the heart optimistically and
   * needs to know to put it back. Swallowing the error here would leave a
   * filled heart on a like the server never recorded, which is the one outcome
   * worse than the tap not registering at all.
   *
   * The list is NOT rewritten on success: the card owns its own count while it
   * is on screen, and replacing the post object here would fight it.
   */
  const toggleLike = (post: FeedPost, on: boolean) => app.backend.toggleLike(post.id, on);

  // --- private account ------------------------------------------------------
  if (!socialOn) {
    return (
      <div className="screen">
        <ScreenHeader title="Feed" />
        <div className="card col" style={{ gap: 12, marginTop: 8 }}>
          <div className="row">
            <span className="chip chip--fact" style={{ cursor: 'default' }}>
              🔒 {t('social.private.badge')}
            </span>
          </div>
          <div className="h2">{t('social.private.title')}</div>
          <div className="small muted" style={{ lineHeight: 1.6 }}>
            {t('social.private.body')}
          </div>
          <div className="small muted" style={{ lineHeight: 1.6 }}>
            The feed is where members write to each other. It is switched off with everything else
            in the social layer — nobody there can see you, and nothing they write reaches this
            device.
          </div>
          <Link to="/social/enable">
            <button className="btn btn--primary btn--block">{t('social.private.cta')}</button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="screen">
      <ScreenHeader
        title="Feed"
        subtitle="What members are saying today"
        right={
          <Link to="/discover" className="chip">
            {t('tabs.discover')} →
          </Link>
        }
      />

      <PostComposer onPosted={loadFirst} />

      {failed && (
        <div className="banner banner--danger small spread" style={{ marginBottom: 12 }}>
          <span>The feed didn’t load. Check your connection.</span>
          <button className="chip" onClick={() => void loadFirst()}>
            {t('common.retry')}
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ display: 'grid', placeItems: 'center', padding: '32px 0' }}>
          <div className="spinner" />
        </div>
      ) : posts.length === 0 ? (
        /* THE EMPTY STATE, which has to sell the feature without overselling
           it. It says what this is (members only, written by people, no
           algorithm), what it costs (nothing, for text), and what to do — and
           it does not promise an audience that may not be there yet. */
        <div className="card col" style={{ gap: 10, lineHeight: 1.6 }}>
          <div className="h2">Nobody has posted yet</div>
          <div className="small muted">
            This is the only place on Astrally where members write to each other in the open.
            Everything else — your chart, your readings, your conversations with Ally — is yours
            alone, and that does not change.
          </div>
          <div className="small muted">
            It is only visible to people who have turned connections on, there is no algorithm
            deciding what you see, and posts appear newest first. Writing one is free. Be the first
            — a sentence about your week is enough.
          </div>
          <div className="tiny faint">{t('safety.disclaimer')}</div>
        </div>
      ) : (
        <>
          <div className="col" style={{ gap: 0 }}>
            {posts.map((post) => (
              <PostCard
                key={post.id}
                post={post}
                isPro={isPro}
                onDelete={remove}
                onReport={report}
                onToggleLike={toggleLike}
              />
            ))}
          </div>

          {cursor ? (
            <button
              className="btn btn--ghost btn--block"
              disabled={loadingMore}
              onClick={loadMore}
              style={{ marginTop: 4 }}
            >
              {loadingMore ? <span className="spinner" /> : 'Show older posts'}
            </button>
          ) : (
            <div className="tiny faint center" style={{ padding: '14px 0' }}>
              That’s everything for now.
            </div>
          )}
        </>
      )}
    </div>
  );
}
